import { AnimatePresence } from "moti";
import React, {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useWindowDimensions } from "react-native";
import { v4 as uuidv4 } from "uuid";

import { VStack } from "../../primitive";
import ToastWrapper from "./ToastWrapper";
import {
  ToastContext,
  ToastRegistryContext,
  iToastContext,
  iToastRegistryContext,
} from "./toastContext";
import { ShowToastProps } from "./types";

const COMPACT_VIEWPORT_WIDTH = 368;
const COMPACT_HORIZONTAL_OFFSET = 8;
const DEFAULT_HORIZONTAL_OFFSET = 16;
const MAX_TOAST_WIDTH = 400;
export const TOAST_OVERLAY_Z_INDEX = 9999;

export const getToastListLayout = (windowWidth: number) => {
  const horizontalOffset =
    windowWidth < COMPACT_VIEWPORT_WIDTH
      ? COMPACT_HORIZONTAL_OFFSET
      : DEFAULT_HORIZONTAL_OFFSET;
  const toastListWidth = Math.max(
    0,
    Math.min(MAX_TOAST_WIDTH, windowWidth - horizontalOffset * 2)
  );

  return { horizontalOffset, toastListWidth };
};

type WhooshToastProviderProps = {
  children: ReactNode;
};

/**
 * Provides toast functionality to its subtree.
 *
 * Providers form a chain: each nested provider registers itself with the
 * provider above it. When `showToast` is called, the call is routed to the
 * *deepest* currently-mounted provider, so a toast triggered from anywhere
 * (even outside a modal) renders on the topmost surface. When a nested
 * provider unmounts (e.g. its modal closes), active toasts migrate to the
 * parent provider so success feedback can outlive the modal.
 */
export const WhooshToastProvider = ({ children }: WhooshToastProviderProps) => {
  const parentRegistry = useContext(ToastRegistryContext);

  // Local handlers owned by this provider's ToastListManager. Null until the
  // manager has mounted and reported them back up.
  const [localToastFunctions, setLocalToastFunctions] =
    useState<iToastContext | null>(null);
  const localRef = useRef<iToastContext | null>(null);
  localRef.current = localToastFunctions;

  // Effects in child consumers can request a toast before ToastListManager's
  // initialization effect runs. Retain those requests until the manager can
  // accept them instead of silently dropping them.
  const pendingRoutedToastsRef = useRef<ShowToastProps[]>([]);
  const pendingLocalToastsRef = useRef<ShowToastProps[]>([]);

  // Nested child providers, ordered by registration. The last entry is the
  // deepest/topmost surface.
  const childStackRef = useRef<iToastContext[]>([]);

  // Effective handlers exposed to consumers. Stable across renders: they read
  // the child stack and local handlers via refs at call time.
  const add = useCallback((args: ShowToastProps) => {
    const stack = childStackRef.current;
    const deepestChild = stack[stack.length - 1];
    if (deepestChild) {
      deepestChild.add(args);
    } else if (localRef.current) {
      localRef.current.add(args);
    } else {
      pendingRoutedToastsRef.current = [
        ...pendingRoutedToastsRef.current,
        args,
      ];
    }
  }, []);

  const remove = useCallback((toastId: string) => {
    // Broadcast down: the toast lives in whichever provider rendered it, and
    // RemoveToast is a no-op for unknown ids, so this is safe and idempotent.
    childStackRef.current.forEach((child) => child.remove(toastId));
    pendingRoutedToastsRef.current = pendingRoutedToastsRef.current.filter(
      (toast) => toast.toastId !== toastId
    );
    pendingLocalToastsRef.current = pendingLocalToastsRef.current.filter(
      (toast) => toast.toastId !== toastId
    );
    localRef.current?.remove(toastId);
  }, []);

  const toastFunctions = useMemo<iToastContext>(
    () => ({ add, remove }),
    [add, remove]
  );

  // Registry exposed to nested providers so they can register themselves.
  const register = useCallback((handlers: iToastContext) => {
    childStackRef.current = [...childStackRef.current, handlers];
    return () => {
      childStackRef.current = childStackRef.current.filter(
        (h) => h !== handlers
      );
    };
  }, []);

  const addToLocal = useCallback((args: ShowToastProps) => {
    if (localRef.current) {
      localRef.current.add(args);
    } else {
      pendingLocalToastsRef.current = [...pendingLocalToastsRef.current, args];
    }
  }, []);

  const registry = useMemo(
    () => ({ register, addToLocal }),
    [register, addToLocal]
  );

  const initializeLocalToastFunctions = useCallback(
    (functions: iToastContext) => {
      localRef.current = functions;
      setLocalToastFunctions(functions);

      const pendingLocalToasts = pendingLocalToastsRef.current;
      pendingLocalToastsRef.current = [];
      pendingLocalToasts.forEach((toast) => functions.add(toast));

      const pendingRoutedToasts = pendingRoutedToastsRef.current;
      pendingRoutedToastsRef.current = [];
      pendingRoutedToasts.forEach(add);
    },
    [add]
  );

  // Register this provider's effective handlers with the parent provider (if
  // any). `toastFunctions` and `parentRegistry` are stable, so this runs once
  // on mount and cleans up on unmount.
  useEffect(() => {
    if (!parentRegistry) {
      return;
    }
    return parentRegistry.register(toastFunctions);
  }, [parentRegistry, toastFunctions]);

  return (
    <ToastContext.Provider value={toastFunctions}>
      <ToastRegistryContext.Provider value={registry}>
        {children}
        <MemoizedToastListManager
          setToastFunctions={initializeLocalToastFunctions}
          parentRegistry={parentRegistry}
        />
      </ToastRegistryContext.Provider>
    </ToastContext.Provider>
  );
};

const ToastListManager = ({
  setToastFunctions,
  parentRegistry,
}: {
  setToastFunctions: (functions: iToastContext) => void;
  parentRegistry: iToastRegistryContext | null;
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [currentToastState, dispatch] = useReducer(toastReducer, {
    toasts: [],
  });
  // Kept in sync with reducer state inside add/remove so unmount cleanup can
  // migrate toasts even when showToast and modal close happen in the same tick
  // (reducer state would not have flushed yet).
  const toastsRef = useRef<Toast[]>([]);
  const { horizontalOffset, toastListWidth } = getToastListLayout(windowWidth);

  const remove = useCallback((id: string) => {
    toastsRef.current = toastsRef.current.filter((t) => t.id !== id);
    dispatch({ type: "RemoveToast", id });
  }, []);

  const add = useCallback(
    (args: ShowToastProps) => {
      const {
        toastId,
        title,
        description,
        rightButton,
        semanticStyle,
        duration,
        canDismiss,
        shouldAutoDismiss,
        testID,
      } = args;
      const id = toastId || uuidv4();
      const newToast = (
        <ToastWrapper
          {...{
            id,
            semanticStyle,
            duration,
            title,
            description,
            rightButton,
            canDismiss,
            shouldAutoDismiss,
            testID,
          }}
          closeToast={remove}
        />
      );
      const props: ShowToastProps = { ...args, toastId: id };
      const toast = { id, content: newToast, props };
      const withoutDuplicate = toastsRef.current.filter((t) => t.id !== id);
      toastsRef.current = [...withoutDuplicate, toast];
      dispatch({ type: "AddToast", toast });
    },
    [remove]
  );

  useEffect(() => {
    if (!hasInitialized) {
      setToastFunctions({ add, remove });
      setHasInitialized(true);
    }
  }, [add, remove, setToastFunctions, hasInitialized]);

  // When this provider unmounts (e.g. modal close), re-home active toasts on the
  // parent so feedback like "saved successfully" survives after onClose().
  useEffect(() => {
    return () => {
      if (!parentRegistry) {
        return;
      }
      const toastsToMigrate = [...toastsRef.current];
      toastsToMigrate.forEach((toast) => {
        parentRegistry.addToLocal(toast.props);
      });
    };
  }, [parentRegistry]);

  return (
    <VStack
      position={"absolute"}
      left={horizontalOffset}
      bottom={24}
      width={toastListWidth}
      style={{ gap: 8, zIndex: TOAST_OVERLAY_Z_INDEX }}
    >
      <AnimatePresence>
        {currentToastState.toasts.slice(0, 10).map((t) => (
          <React.Fragment key={t.id}>{t.content}</React.Fragment>
        ))}
      </AnimatePresence>
    </VStack>
  );
};

const MemoizedToastListManager = memo(ToastListManager);

type ToastProviderState = {
  toasts: Toast[];
};
export type Toast = {
  content: ReactNode;
  id: string;
  props: ShowToastProps;
};

type ToastActions =
  | { type: "AddToast"; toast: Toast }
  | { type: "RemoveToast"; id: string };

const toastReducer = (state: ToastProviderState, action: ToastActions) => {
  switch (action.type) {
    case "AddToast": {
      const newId = action.toast.id ? action.toast.id : uuidv4();
      const newToast = { ...action.toast, id: newId };
      // Replace an existing toast with the same id so the same success message
      // can be shown again (e.g. after migrating from a modal, then saving again).
      const withoutDuplicate = state.toasts.filter((t) => t.id !== newId);
      return { ...state, toasts: [...withoutDuplicate, newToast] };
    }
    case "RemoveToast": {
      if (state.toasts.some((t) => t.id === action.id)) {
        const toasts = state.toasts.filter((t) => t.id !== action.id);
        return { ...state, toasts: toasts };
      }
      return state;
    }
    default:
      return state;
  }
};
